import { useEffect } from 'react';

/* ── Toast ── */
export function Toast({ message, severity = 'success', onClose }) {
  useEffect(() => { const t = setTimeout(onClose, 5000); return () => clearTimeout(t); }, [onClose]);
  const c = {
    success: { bg: '#f0fdf4', border: '#a7f3d0', text: '#065f46', icon: '#10b981' },
    error:   { bg: '#fef2f2', border: '#fecaca', text: '#b91c1c', icon: '#ef4444' },
    warning: { bg: '#fffbeb', border: '#fde68a', text: '#92400e', icon: '#f59e0b' },
    info:    { bg: '#eff6ff', border: '#bfdbfe', text: '#1d4ed8', icon: '#3b82f6' },
  }[severity] || { bg: '#f0fdf4', border: '#a7f3d0', text: '#065f46', icon: '#10b981' };
  const tick = <svg style={{ width: 12, height: 12, color: 'white' }} fill="none" viewBox="0 0 24 24" strokeWidth={3} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg>;
  const cross = <svg style={{ width: 12, height: 12, color: 'white' }} fill="none" viewBox="0 0 24 24" strokeWidth={3} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>;
  return (
    <div style={{ position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', zIndex: 9999, maxWidth: 420, width: 'calc(100% - 48px)', background: c.bg, border: `1px solid ${c.border}`, borderRadius: 14, padding: '14px 16px', boxShadow: '0 8px 32px rgba(0,0,0,0.12)', display: 'flex', alignItems: 'center', gap: 12 }}>
      <div style={{ width: 20, height: 20, borderRadius: '50%', background: c.icon, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        {severity === 'success' ? tick : cross}
      </div>
      <p style={{ flex: 1, fontSize: 13, fontWeight: 500, color: c.text, margin: 0, lineHeight: 1.5 }}>{message}</p>
      <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: c.text, opacity: 0.5, padding: 0, display: 'flex', flexShrink: 0 }}>
        <svg style={{ width: 16, height: 16 }} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
      </button>
    </div>
  );
}

/* ── Modal ── */
export function Modal({ open, onClose, title, children, footer }) {
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);
  if (!open) return null;
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div onClick={onClose} style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(2px)' }} />
      <div style={{ position: 'relative', background: 'white', borderRadius: 20, width: '100%', maxWidth: 460, boxShadow: '0 24px 60px rgba(0,0,0,0.18)', overflow: 'hidden', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px', borderBottom: '1px solid #f1f5f1', flexShrink: 0 }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#1e293b' }}>{title}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: 4, borderRadius: 8, display: 'flex' }}>
            <svg style={{ width: 18, height: 18 }} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>
        <div style={{ padding: '20px 24px', overflowY: 'auto', flex: 1 }}>{children}</div>
        {footer && <div style={{ padding: '16px 24px', borderTop: '1px solid #f1f5f1', display: 'flex', gap: 10, justifyContent: 'flex-end', flexShrink: 0 }}>{footer}</div>}
      </div>
    </div>
  );
}

/* ── Form field wrapper ── */
export function Field({ label, hint, error, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {label && <label style={{ fontSize: 13, fontWeight: 700, color: '#475569' }}>{label}</label>}
      {children}
      {error && <p style={{ fontSize: 11, color: '#ef4444', margin: 0, fontWeight: 500 }}>{error}</p>}
      {hint && !error && <p style={{ fontSize: 11, color: '#94a3b8', margin: 0 }}>{hint}</p>}
    </div>
  );
}

/* ── Detail row for info modals ── */
export function DetailRow({ label, value }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '10px 0', borderBottom: '1px solid #f8fafc', gap: 12 }}>
      <span style={{ fontSize: 13, color: '#94a3b8', fontWeight: 600, flexShrink: 0 }}>{label}</span>
      <span style={{ fontSize: 13, color: '#1e293b', fontWeight: 600, textAlign: 'right', wordBreak: 'break-word' }}>{value || '—'}</span>
    </div>
  );
}

/* ── Btn helpers ── */
export function BtnPrimary({ children, onClick, type = 'button', disabled, style }) {
  return (
    <button type={type} onClick={onClick} disabled={disabled}
      style={{ padding: '9px 20px', borderRadius: 10, border: 'none', background: 'linear-gradient(135deg,#10b981,#059669)', color: 'white', fontSize: 13, fontWeight: 700, cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.65 : 1, ...style }}>
      {children}
    </button>
  );
}
export function BtnSecondary({ children, onClick, type = 'button', style }) {
  return (
    <button type={type} onClick={onClick}
      style={{ padding: '9px 20px', borderRadius: 10, border: '1.5px solid #e2e8f0', background: 'white', color: '#475569', fontSize: 13, fontWeight: 600, cursor: 'pointer', ...style }}>
      {children}
    </button>
  );
}
export function BtnDanger({ children, onClick }) {
  return (
    <button type="button" onClick={onClick}
      style={{ padding: '9px 20px', borderRadius: 10, border: 'none', background: 'linear-gradient(135deg,#ef4444,#dc2626)', color: 'white', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
      {children}
    </button>
  );
}

/* ── Product search input + dropdown ── */
export function ProductSearch({ value, onChange, onSelect, onBlur, onFocus, results, placeholder = 'Search by product name…' }) {
  return (
    <div style={{ position: 'relative' }}>
      <svg style={{ position: 'absolute', left: 13, top: '50%', transform: 'translateY(-50%)', width: 16, height: 16, color: '#94a3b8', pointerEvents: 'none' }} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
      </svg>
      <input className="v2-input" style={{ paddingLeft: 40 }} type="text" placeholder={placeholder} value={value} onChange={e => onChange(e.target.value)} onBlur={onBlur} onFocus={onFocus} autoComplete="off" />
      {results && results.length > 0 && (
        <div style={{ marginTop: 4, border: '1.5px solid #e2e8f0', borderRadius: 12, overflow: 'hidden', background: 'white', boxShadow: '0 8px 24px rgba(0,0,0,0.08)' }}>
          {results.map((p, i) => (
            <button key={i} type="button" onMouseDown={() => onSelect(p)}
              style={{ display: 'flex', flexDirection: 'column', width: '100%', padding: '11px 16px', background: 'none', border: 'none', borderBottom: i < results.length - 1 ? '1px solid #f1f5f9' : 'none', cursor: 'pointer', textAlign: 'left' }}
              onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
              onMouseLeave={e => e.currentTarget.style.background = 'none'}>
              <span style={{ fontSize: 13.5, fontWeight: 600, color: '#1e293b' }}>{p.product_name}</span>
              <span style={{ fontSize: 11.5, color: '#94a3b8', marginTop: 2 }}>
                {[p.brand && `Brand: ${p.brand}`, p.variety && `Variety: ${p.variety}`, p.size && `Size: ${p.size}`].filter(Boolean).join(' · ') || 'No details'}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Page header with back button ── */
export function PageHeader({ title, subtitle, backTo, onBack, action }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', marginBottom: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        {(backTo || onBack) && (
          <button onClick={onBack} style={{ width: 36, height: 36, borderRadius: 10, border: '1.5px solid #e2e8f0', background: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b', flexShrink: 0 }}>
            <svg style={{ width: 16, height: 16 }} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" /></svg>
          </button>
        )}
        <div>
          <h1 style={{ color: '#0f172a', fontSize: 20, fontWeight: 800, margin: 0, letterSpacing: '-0.02em' }}>{title}</h1>
          {subtitle && <p style={{ color: '#94a3b8', fontSize: 13, margin: '2px 0 0' }}>{subtitle}</p>}
        </div>
      </div>
      {action}
    </div>
  );
}

/* ── Loading spinner ── */
export function LoadingSpinner({ message = 'Loading…' }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ width: 40, height: 40, borderRadius: '50%', border: '3px solid #e2e8f0', borderTopColor: '#10b981', animation: 'spin 0.8s linear infinite', margin: '0 auto 16px' }} />
        <p style={{ color: '#64748b', fontSize: 14, margin: 0 }}>{message}</p>
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

/* ── Empty state ── */
export function EmptyState({ message, icon }) {
  return (
    <div className="v2-card" style={{ padding: 48, textAlign: 'center' }}>
      <div style={{ width: 48, height: 48, borderRadius: '50%', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
        {icon || <svg style={{ width: 24, height: 24, color: '#94a3b8' }} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" /></svg>}
      </div>
      <p style={{ color: '#64748b', fontSize: 14, margin: 0 }}>{message}</p>
    </div>
  );
}

/* ── Pagination bar ── */
export function Pagination({ page, totalPages, pageSize, totalItems, onPageChange, onPageSizeChange, pageSizes = [6, 12, 18] }) {
  if (totalPages <= 1) return null;
  const start = page * pageSize + 1;
  const end = Math.min((page + 1) * pageSize, totalItems);
  return (
    <div className="v2-card" style={{ padding: '12px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 13, color: '#64748b' }}>Rows per page:</span>
        <div style={{ position: 'relative' }}>
          <select className="v2-input" style={{ width: 'auto', padding: '6px 28px 6px 10px', fontSize: 13, appearance: 'none' }} value={pageSize} onChange={e => onPageSizeChange(Number(e.target.value))}>
            {pageSizes.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <svg style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', width: 12, height: 12, color: '#94a3b8', pointerEvents: 'none' }} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" /></svg>
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ fontSize: 13, color: '#64748b' }}>{start}–{end} of {totalItems}</span>
        <button onClick={() => onPageChange(page - 1)} disabled={page === 0}
          style={{ width: 32, height: 32, borderRadius: 8, border: '1px solid #e2e8f0', background: page === 0 ? '#f8fafc' : 'white', cursor: page === 0 ? 'default' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: page === 0 ? '#cbd5e1' : '#475569' }}>
          <svg style={{ width: 14, height: 14 }} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" /></svg>
        </button>
        <button onClick={() => onPageChange(page + 1)} disabled={page >= totalPages - 1}
          style={{ width: 32, height: 32, borderRadius: 8, border: '1px solid #e2e8f0', background: page >= totalPages - 1 ? '#f8fafc' : 'white', cursor: page >= totalPages - 1 ? 'default' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: page >= totalPages - 1 ? '#cbd5e1' : '#475569' }}>
          <svg style={{ width: 14, height: 14 }} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" /></svg>
        </button>
      </div>
    </div>
  );
}

/* ── Row action buttons (edit, info, delete) ── */
export function ActionButtons({ onEdit, onInfo, onDelete }) {
  const btn = (onClick, hoverBorder, hoverColor, hoverBg, icon) => (
    <button onClick={onClick} type="button" style={{ width: 32, height: 32, borderRadius: 8, border: '1px solid #e2e8f0', background: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b', transition: 'all 0.12s' }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = hoverBorder; e.currentTarget.style.color = hoverColor; if (hoverBg) e.currentTarget.style.background = hoverBg; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.color = '#64748b'; e.currentTarget.style.background = 'white'; }}>
      {icon}
    </button>
  );
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
      {onEdit && btn(onEdit, '#10b981', '#10b981', null, <svg style={{ width: 14, height: 14 }} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931z" /></svg>)}
      {onInfo && btn(onInfo, '#60a5fa', '#3b82f6', null, <svg style={{ width: 14, height: 14 }} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" /></svg>)}
      {onDelete && btn(onDelete, '#fecaca', '#ef4444', '#fef2f2', <svg style={{ width: 14, height: 14 }} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" /></svg>)}
    </div>
  );
}

/* ── Delete confirm modal ── */
export function DeleteConfirmModal({ open, onClose, onConfirm, itemDescription }) {
  return (
    <Modal open={open} onClose={onClose} title="Confirm Deletion"
      footer={<><BtnSecondary onClick={onClose}>Cancel</BtnSecondary><BtnDanger onClick={onConfirm}>Delete</BtnDanger></>}>
      <p style={{ margin: '0 0 12px', fontSize: 14, color: '#475569', lineHeight: 1.6 }}>Are you sure? This action cannot be undone.</p>
      {itemDescription && (
        <div style={{ padding: '12px 16px', borderRadius: 10, background: '#fef2f2', border: '1px solid #fecaca' }}>
          <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: '#b91c1c' }}>{itemDescription}</p>
        </div>
      )}
    </Modal>
  );
}

/* ── Search bar card ── */
export function SearchBar({ value, onChange, placeholder }) {
  return (
    <div className="v2-card" style={{ padding: '14px 18px' }}>
      <div style={{ position: 'relative' }}>
        <svg style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', width: 16, height: 16, color: '#94a3b8', pointerEvents: 'none' }} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
        </svg>
        <input className="v2-input" style={{ paddingLeft: 38 }} type="text" placeholder={placeholder} value={value} onChange={e => onChange(e.target.value)} />
        {value && (
          <button onClick={() => onChange('')} style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: 0, display: 'flex' }}>
            <svg style={{ width: 16, height: 16 }} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        )}
      </div>
    </div>
  );
}

export function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}
